import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useGetTerminalDetails } from "~/features/quote/hooks/use-terminal-details-hooks.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { Terminal } from "~/shared/schemas/terminal-schema.ts";

export type UseCarrierCardStateParams = {
	carrierPrice: CarrierPrice;
};

export type UseCarrierCardStateReturn = {
	cardRef: React.RefObject<HTMLDivElement>;
	isExpanded: boolean;
	isAdHocCarrier: boolean;
	setIsExpanded: (expanded: boolean) => void;
	handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export function useCarrierCardState({
	carrierPrice,
}: UseCarrierCardStateParams): UseCarrierCardStateReturn {
	const cardRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);

	const isAdHocCarrier: boolean = useMemo(
		() => Boolean(carrierPrice.isAdHoc),
		[carrierPrice.isAdHoc],
	);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			event.currentTarget.focus();
		}
	};

	return {
		cardRef,
		isExpanded,
		isAdHocCarrier,
		setIsExpanded,
		handleKeyDown,
	};
}

/**
 * Hook to validate carrier price completeness
 * @param carrierPrice - The carrier price object to validate
 * @returns Object containing validation state
 */
export function useCarrierValidation(carrierPrice: CarrierPrice) {
	const isDisabled = useMemo(() => {
		return carrierPrice.carrier.id < 1;
	}, [carrierPrice.carrier.id]);

	const isComplete = useMemo(() => {
		const isCarrierSelected =
			typeof carrierPrice.carrierId === "number" && carrierPrice.carrierId > 0;
		const isCarrierRateValid =
			typeof carrierPrice.carrierRate === "number" &&
			carrierPrice.carrierRate > 0;
		const isBrokerMarginValid =
			typeof carrierPrice.brokerMargin === "number" &&
			carrierPrice.brokerMargin > 0;
		const isServiceLevelValid =
			!!carrierPrice.serviceLevel && (carrierPrice.serviceLevel.id ?? 0) > 0;
		const isTransitDaysValid =
			typeof carrierPrice.transitDays === "number" &&
			carrierPrice.transitDays > 0;
		const isExpirationValid =
			!!carrierPrice.carrierQuoteExpirationDate &&
			carrierPrice.carrierQuoteExpirationDate !== "";
		const isCarrierQuoteIdValid =
			!!carrierPrice.carrierQuoteId &&
			carrierPrice.carrierQuoteId.trim() !== "";

		return (
			isCarrierSelected &&
			isCarrierRateValid &&
			isBrokerMarginValid &&
			isServiceLevelValid &&
			isTransitDaysValid &&
			isExpirationValid &&
			isCarrierQuoteIdValid
		);
	}, [
		carrierPrice.carrierId,
		carrierPrice.carrierRate,
		carrierPrice.brokerMargin,
		carrierPrice.serviceLevel,
		carrierPrice.transitDays,
		carrierPrice.carrierQuoteExpirationDate,
		carrierPrice.carrierQuoteId,
	]);

	return {
		isDisabled,
		isComplete,
	};
}

export type UseCarrierTerminalsParams = {
	carrierPrice: CarrierPrice;
	isExpanded: boolean;
};

export type UseCarrierTerminalsReturn = {
	originTerminal: Terminal | undefined;
	destinationTerminal: Terminal | undefined;
	isTerminalDetailsLoading: boolean;
};

export function useCarrierTerminals({
	carrierPrice,
	isExpanded,
}: UseCarrierTerminalsParams): UseCarrierTerminalsReturn {
	const { quoteState } = useContext(QuoteDetailsContext);
	const [originTerminal, setOriginTerminal] = useState<Terminal>();
	const [destinationTerminal, setDestinationTerminal] = useState<Terminal>();

	const { data: terminalDetails, isLoading: isTerminalDetailsLoading } =
		useGetTerminalDetails(
			carrierPrice.originTerminalCode,
			carrierPrice.destinationTerminalCode,
			quoteState.shipmentDate,
			carrierPrice.carrier.scac ?? "",
			carrierPrice.carrier.id,
			carrierPrice.serviceAreaTypeId?.toString() ?? null,
			isExpanded,
		);

	useEffect(() => {
		setOriginTerminal(
			terminalDetails?.terminals.find(
				(x: Terminal) => x.code === carrierPrice.originTerminalCode,
			),
		);
		setDestinationTerminal(
			terminalDetails?.terminals.find(
				(x: Terminal) => x.code === carrierPrice.destinationTerminalCode,
			),
		);
	}, [
		carrierPrice.destinationTerminalCode,
		carrierPrice.originTerminalCode,
		terminalDetails?.terminals,
	]);

	return {
		originTerminal,
		destinationTerminal,
		isTerminalDetailsLoading,
	};
}
